import { Cookie } from "src/utils/cookies";
import { referralUrlOrDefault } from "../tokens";
import { ContentCacheUtils } from "src/utils/storage.util";

declare const window: { dataLayer: any, dispatchEvent: (ev: CustomEvent) => void, location: Location };
export namespace GA4 {

    export function track(detail: GA4) {

        window.dispatchEvent(new CustomEvent('track', { detail }));
    }

    export interface GA4 {
        __event_name: string;
    }
    /**
     * Virtual page view
     */
    export interface VirtualPageview extends GA4 {
        __event_name: 'virtual_pageview';
    }
    /**
     * @name item
     * @param affiliation
     * @param coupon
     * @param discount combined discount
     * @param item_brand
     * @param item_category one required use multi if sections max 5
     * @param item_category2
     * @param item_category3
     * @param item_category4
     * @param item_category5
     * @param item_id sku
     * @param item_name
     * @param item_variant
     * @param price list price
     * @param quantity of change
     * @param index
     * @param location_id
     * @param item_list_id
     * @param item_list_name
     * @param promotion_id
     * @param promotion_name
     * @param creative_name
     * @param creative_slot
     */
    export interface Item extends GA4 {
        __event_name: 'item';
        affiliation?: string;
        coupon?: string;
        discount?: string | number;
        item_brand?: string;
        item_category?: string;
        item_category2?: string;
        item_category3?: string;
        item_category4?: string;
        item_category5?: string;
        item_id: string;
        item_name: string;
        item_variant?: string;
        price?: string | number;
        quantity?: string | number;
        index?: number;
        location_id?: string;
        item_list_id?: string;
        item_list_name?: string;
        promotion_id?: string;
        promotion_name?: string;
        creative_name?: string;
        creative_slot?: string;
    }

    export interface ItemArray extends GA4 {
        items?: Array<Omit<Item, '__event_name'>>;
    }

    /**
     * @name view_item
     * Only one item
     */
    export interface ViewItem extends GA4, ItemArray {
        __event_name: 'view_item';
        currency?: string;
        items?: Array<Omit<Item, '__event_name'>>;
        value?: string | number;
    }
    /**
     * @name view_item_list
     */
    export interface ViewItemList extends GA4, ItemArray {
        __event_name: 'view_item_list';
        items?: Array<Omit<Item, '__event_name'>>;
        item_list_name?: string;
        item_list_id?: string;
    }
    /**
     * @name select_item
     * Only one item
     */
    export interface SelectItem extends GA4, ItemArray {
        __event_name: 'select_item';
        items?: Array<Omit<Item, '__event_name'>>;
        item_list_name?: string;
        item_list_id?: string;
    }
    /**
     * @name add_to_wishlist
     */
    export interface AddToWishlist extends GA4, ItemArray {
        __event_name: 'add_to_wishlist';
        currency?: string;
        items?: Array<Omit<Item, '__event_name'>>;
        value?: string | number;
        basketId?: string;
    }
    /**
     * @name add_to_cart
     */
    export interface AddToCart extends GA4, ItemArray {
        __event_name: 'add_to_cart';
        currency?: string;
        items?: Array<Omit<Item, '__event_name'>>;
        value?: string | number;
        basketId?: string;
    }
    /**
     * @name view_promotion
     */
    export interface ViewPromotion extends GA4, ItemArray {
        __event_name: 'view_promotion';
        items?: Array<Omit<Item, '__event_name'>>;
        promotion_id?: string;
        promotion_name?: string;
        creative_name?: string;
        creative_slot?: string;
        location_id?: string;
    }
    /**
     * @name select_promotion
     */
    export interface SelectPromotion extends GA4, ItemArray {
        __event_name: 'select_promotion';
        items?: Array<Omit<Item, '__event_name'>>;
        promotion_id?: string;
        promotion_name?: string;
        creative_name?: string;
        creative_slot?: string;
        location_id?: string;
    }
    /**
     * @name view_cart
     */
    export interface ViewCart extends GA4, ItemArray {
        __event_name: 'view_cart';
        currency?: string;
        items?: Array<Omit<Item, '__event_name'>>;
        value?: string | number;
        basketId?: string;
    }
    /**
     * @name remove_from_cart
     */
    export interface RemoveFromCart extends GA4, ItemArray {
        __event_name: 'remove_from_cart';
        currency?: string;
        items?: Array<Omit<Item, '__event_name'>>;
        value?: string | number;
        basketId?: string;
    }

    export interface Checkout extends GA4, ItemArray {
        currency?: string;
        items?: Array<Omit<Item, '__event_name'>>;
        value?: string | number;
        coupon?: string;
        basketId?: string;
    }

    /**
     * @name begin_checkout
     */
    export interface Search extends Checkout {
        __event_name: 'search';
        search_term: string;
    }
    /**
     * @name begin_checkout
     */
    export interface BeginCheckout extends Checkout {
        __event_name: 'begin_checkout';
        basketId?: string;
    }
    /**
     * @name add_payment_info
     */
    export interface AddPaymentInfo extends Checkout {
        __event_name: 'add_payment_info';
        payment_type: string;
        basketId?: string;
    }
    /**
     * @name add_shipping_info
     */
    export interface AddShippingInfo extends Checkout {
        __event_name: 'add_shipping_info';
        shipping_tier: string;
        extras?: string[];
        basketId?: string;
    }
    /**
     * @name purchase
     */
    export interface Purcahse extends Checkout {
        __event_name: 'purchase';
        affiliation?: string;
        transaction_id?: string;
        order_number?: string;
        shipping?: number | string;
        tax?: number | string;
        basketId?: string;
        user_data?: Record<string, Record<string, string> | string>;
    }
    /**
     * @name refund
     */
    export interface Refund extends GA4 {
        __event_name: 'refund';
        affiliation: string;
        coupon: string;
        currency: string;
        items: Array<Omit<Item, '__event_name'>>;
        transaction_id: string;
        shipping: string;
        tax: number | string;
        value: string | number;
        basketId?: string;
    }

    /**
     * @name earn_virtual_currency
     */
    export interface EarnVirtualCurrency extends GA4 {
        __event_name: 'earn_virtual_currency';
        virtual_currency_name: string;
        value: string | number;
    }

    /**
     * @name generate_lead
     */
    export interface GenerateLead extends GA4 {
        __event_name: 'generate_lead';
        currency: string;
        value: string | number;
    }

    /**
     * @name join_group
     */
    export interface JoinGroup extends GA4 {
        __event_name: 'join_group';
        group_id: string;
    }
    /**
     * @name level_end
     */
    export interface LevelEnd extends GA4 {
        __event_name: 'level_end';
        level_name: string;
        success: boolean;
    }
    /**
     * @name level_start
     */
    export interface LevelStart extends GA4 {
        __event_name: 'level_start';
        level_name: string;
    }
    /**
     * @name level_up
     */
    export interface LevelUp extends GA4 {
        __event_name: 'level_up';
        level: number;
        character: string;
    }
    /**
     * @name login
     */
    export interface Login extends GA4 {
        __event_name: 'login';
        method: string;
    }
    /**
     * @name post_score
     */
    export interface PostScore extends GA4 {
        __event_name: 'post_score';
        score: number;
        level: number;
        character: string;
    }
    /**
     * @name search
     */
    export interface Search extends GA4 {
        __event_name: 'search';
        search_term: string;
    }
    /**
     * @name select_content
     */
    export interface SelectContent extends GA4 {
        __event_name: 'select_content';
        content_type: string;
        content_id: string;
    }
    /**
     * @name share
     */
    export interface Share extends GA4 {
        __event_name: 'share';
        method: string;
        content_type: string;
        item_id: string;
    }
    /**
     * @name sign_up
     */
    export interface SignUp extends GA4 {
        __event_name: 'sign_up';
        method: string;
    }
    /**
     * @name spend_virtual_currency
     */
    export interface SpendVirtualCurrency extends GA4 {
        __event_name: 'spend_virtual_currency';
        value: number;
        virtual_currency_name: string;
        item_name: string;
    }
    /**
     * @name tutorial_begin
     */
    export interface TutorialBegin extends GA4 {
        __event_name: 'tutorial_begin';
    }
    /**
     * @name tutorial_complete
     */
    export interface TutorialComplete extends GA4 {
        __event_name: 'tutorial_complete';
    }
    /**
     * @name unlock_achievement
     */
    export interface UnlockAchievement extends GA4 {
        __event_name: 'unlock_achievement';
        achievement_id: string;
    }

    export interface Config {
        allow_google_signals?: boolean;
        allow_ad_personalization_signals?: boolean;
        campaign_content?: string;
        campaign_id?: string;
        campaign_medium?: string;
        campaign_name?: string;
        campaign_source?: string;
        campaign_term?: string;
        campaign?: { id: string, source: string, medium: string, name: string, term: string, content: string };
        client_id?: string;
        content_group?: string;
        cookie_domain?: string;
        cookie_expires?: number;
        cookie_flags?: string;
        cookie_path?: string;
        cookie_prefix?: string;
        cookie_update?: boolean;
        language?: string;
        page_location?: string;
        page_referrer?: string;
        page_title?: string;
        send_page_view?: boolean;
        screen_resolution?: string;
        user_id?: string;
        user_properties?: { [key: string]: string }
    }

    export type GA4Event = GA4 |
        Item |
        ViewItem |
        ViewItemList |
        SelectItem |
        AddToWishlist |
        AddToCart |
        ViewPromotion |
        SelectPromotion |
        ViewCart |
        RemoveFromCart |
        BeginCheckout |
        AddPaymentInfo |
        AddShippingInfo |
        Purcahse |
        Refund |
        EarnVirtualCurrency |
        GenerateLead |
        JoinGroup |
        LevelEnd |
        LevelStart |
        LevelUp |
        Login |
        PostScore |
        Search |
        SelectContent |
        Share |
        SignUp |
        SpendVirtualCurrency |
        TutorialBegin |
        TutorialComplete |
        UnlockAchievement;

    export function payload(data: Partial<GA4Event>): object {
        const _data = Object.assign({}, data);
        delete _data.__event_name;
        return {
            event: data.__event_name,
            ecommerce: Object.keys(_data).length > 0 ? _data : null
        }
    }

    export function gtmStart() {
        if (!Array.isArray(window.dataLayer))
            window.dataLayer = [];
        window.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
        return null;
    }

    export function setLocation() {
        let referralUrl = referralUrlOrDefault(Cookie.get('RetusReferral')) ?? new URL(window.location.href);
        if (!Array.isArray(window.dataLayer))
            window.dataLayer = [];
        window.dataLayer.push({ 'originalLocation': referralUrl.toString() });
        return null;
    }

    export function setConsentMode() {
        const script = document.createElement('script');
        script.type = "";
        script.innerHTML = `
        window.dataLayer = window.dataLayer || [];
        function gtag() {
        dataLayer.push(arguments);
        }
        gtag('consent', 'default', {
            'ad_user_data': 'denied',
            'ad_personalization': 'denied',
            ad_storage: 'denied',
            analytics_storage: 'denied',
            functionality_storage: 'denied',
            personalization_storage : 'denied',
            security_storage: 'denied',
            wait_for_update: 1500,
        });
        gtag('set', 'ads_data_redaction', true);
        gtag('set', 'url_passthrough', true);
        `;
        document.head.appendChild(script);
        return null;
    }

    export function setDataLayer() {
        try {
            if (!window.dataLayer || !Array.isArray(window.dataLayer)) {
                window.dataLayer = window.dataLayer || [];
            }
            // NOTE: Rogue Referral Problem (https://www.simoahava.com/gtm-tips/fix-rogue-referral-problem-single-page-sites)
        } catch (e) {}
        return null;
    }

    export function setScript() {
        // setTimeout(() => {
        //     let id = ContentCacheUtils.system<{ googleTagManagerAPIKey: string }>()?.googleTagManagerAPIKey;
        //     const script = document.createElement('script');
        //     const noscript = document.createElement('noscript');
        //     const iframe = document.createElement('iframe');
        //     iframe.src = `https://www.googletagmanager.com/ns.html?id=${id}`;
        //     script.src = `https://www.googletagmanager.com/gtm.js?id=${id}&l=dataLayer`;
        //     script.type = `text/javascript`;
        //     // script.crossOrigin = `anonymous`;
        //     iframe.height = '0';
        //     iframe.width = '0';
        //     iframe.setAttribute('style', 'display:none;visibility:hidden');
        //     noscript.appendChild(iframe);
        //     document.body.appendChild(noscript);
        //     document.head.appendChild(script);
        // }, 100);
        return null;
    }



    export function purchase(ga4Event: Purcahse, times = 0) {
        times++;
        let isTracked = false;
        if (times > 11) {
            console.warn('Tracking of purchase could not occur')
            return;
        }

        //@ts-ignore
        const datalayer = window.dataLayer;
        try {
            const track = datalayer != null && datalayer.some((e: any) => e != null && ((e[1] == 'update' && e[0] == 'consent') || (times == 11 && e[0] == 'consent')))
            if (track)
                window.dataLayer.push({
                    ecommerce: { ...ga4Event },
                    event: "purchase"
                })
            isTracked = track;
        } catch (error) {
            isTracked = false;
        }
        if (!isTracked) {
            setTimeout(() => {
                purchase(ga4Event, times)
            }, 2000);
        }
    }
}
